import React, { useState } from 'react';
import { IonActionSheet, IonToast, IonAlert } from '@ionic/react';
import { camera, images, trash } from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import UserAvatar from './UserAvatar';
import ImageCropEditor from './ImageCropEditor';
import ProfilePhotoService from '../services/ProfilePhotoService';
import UserProfileService from '../services/UserProfileService';
import { connect } from '../data/connect';
import { setProfilePhotoUrl } from '../data/user/user.actions';

interface EditableUserAvatarProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onPhotoUpdated?: (newPhotoUrl: string) => void;
}

interface StateProps {
  userCed?: string;
  eventCode?: string;
  isLoggedIn?: boolean;
}

interface DispatchProps {
  setProfilePhotoUrl: typeof setProfilePhotoUrl;
}

type Props = EditableUserAvatarProps & StateProps & DispatchProps;

/**
 * Componente que combina UserAvatar con funcionalidad de edición
 * Permite cambiar la foto de perfil del usuario logueado
 */
const EditableUserAvatar: React.FC<Props> = ({
  size = 'large',
  className = '',
  onPhotoUpdated,
  userCed,
  eventCode,
  isLoggedIn,
  setProfilePhotoUrl
}) => {
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger' | 'warning'>('success');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCropEditor, setShowCropEditor] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<File | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string>('');

  const profilePhotoService = ProfilePhotoService.getInstance();
  const userProfileService = UserProfileService.getInstance();

  // Manejar click en el avatar para abrir opciones de edición
  const handleEditClick = () => {
    if (!isLoggedIn || !userCed || !eventCode) {
      showToastMessage('Inicia sesión para cambiar tu foto de perfil', 'warning');
      return;
    }
    setShowActionSheet(true);
  };

  // Tomar foto con la cámara
  const takePhoto = async () => {
    setUploading(true);
    try {
      // En PWA, usar input file con capture para mejor compatibilidad
      if (!Capacitor.isNativePlatform()) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.capture = 'environment'; // Usar cámara trasera por defecto

        input.onchange = (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (file) {
            setImageToEdit(file);
            setShowCropEditor(true);
          }
          setUploading(false);
        };

        input.onclick = () => {
          // Si el usuario cancela, resetear el estado
          setTimeout(() => {
            if (!input.files?.length) {
              setUploading(false);
            }
          }, 1000);
        };

        input.click();
        return;
      }

      // Para plataformas nativas, usar Camera plugin
      const result = await profilePhotoService.takePhoto({
        optimizeImage: false // No optimizar aquí, se hará en el editor
      });
      if (result.success && result.photoUrl) {
        // Convertir dataUrl a File para consistencia
        try {
          const response = await fetch(result.photoUrl);
          const blob = await response.blob();
          const file = new File([blob], `camera_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
          setImageToEdit(file);
          setShowCropEditor(true);
        } catch (conversionError) {
          console.error('Error converting photo to file:', conversionError);
          // Fallback: usar URL directamente
          setTempImageUrl(result.photoUrl);
          setShowCropEditor(true);
        }
      } else {
        showToastMessage(result.error || 'Error al tomar la foto', 'danger');
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      showToastMessage('Error al acceder a la cámara', 'danger');
    } finally {
      setUploading(false);
    }
  };

  // Seleccionar foto de la galería
  const selectFromGallery = async () => {
    try {
      // Crear input file para seleccionar imagen
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      input.onchange = (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
          setImageToEdit(file);
          setShowCropEditor(true);
        }
      };

      input.click();
    } catch (error) {
      console.error('Error selecting photo:', error);
      showToastMessage('Error al acceder a la galería', 'danger');
    }
  };

  // Eliminar foto de perfil
  const removePhoto = async () => {
    if (!userCed || !eventCode) return;

    setUploading(true);
    try {
      const success = await userProfileService.removeProfilePhoto(userCed, eventCode);
      if (success) {
        setProfilePhotoUrl('');
        onPhotoUpdated?.('');
        showToastMessage('Foto de perfil eliminada', 'success');
      } else {
        showToastMessage('Error al eliminar la foto', 'danger');
      }
    } catch (error) {
      console.error('Error removing photo:', error);
      showToastMessage('Error al eliminar la foto', 'danger');
    } finally {
      setUploading(false);
    }
  };

  // Manejar imagen recortada
  const handleImageCropped = async (croppedBlob: Blob, croppedDataUrl: string) => {
    if (!userCed || !eventCode) return;

    setUploading(true);
    try {
      // Procesar la imagen recortada
      const result = await profilePhotoService.processCroppedImage(croppedBlob);
      if (result.success && result.photoUrl) {
        // Subir a Firebase
        const success = await userProfileService.updateProfilePhoto(userCed, eventCode, result.photoUrl);
        if (success) {
          setProfilePhotoUrl(result.photoUrl);
          onPhotoUpdated?.(result.photoUrl);
          showToastMessage('Foto de perfil actualizada', 'success');
        } else {
          showToastMessage('Error al guardar la foto', 'danger');
        }
      } else {
        showToastMessage(result.error || 'Error al procesar la imagen', 'danger');
      }
    } catch (error) {
      console.error('Error updating photo:', error);
      showToastMessage('Error al guardar la foto', 'danger');
    } finally {
      setUploading(false);
      // Limpiar estados temporales
      setImageToEdit(null);
      setTempImageUrl('');
    }
  };

  // Cerrar editor de recorte
  const handleCloseCropEditor = () => {
    setShowCropEditor(false);
    setImageToEdit(null);
    setTempImageUrl('');
    setUploading(false);
  };

  // Mostrar mensaje toast
  const showToastMessage = (message: string, color: 'success' | 'danger' | 'warning') => {
    setToastMessage(message);
    setToastColor(color);
    setShowToast(true);
  };

  // Obtener botones del action sheet
  const getActionSheetButtons = () => {
    const buttons = [];

    // Botón de cámara (disponible en móviles nativos y PWA)
    buttons.push({
      text: 'Tomar foto',
      icon: camera,
      handler: takePhoto
    });

    // Botón de galería
    buttons.push({
      text: 'Seleccionar de galería',
      icon: images,
      handler: selectFromGallery
    });

    // Botón de eliminar (solo si hay foto)
    buttons.push({
      text: 'Eliminar foto',
      icon: trash,
      role: 'destructive',
      handler: () => setShowDeleteAlert(true)
    });

    // Botón cancelar
    buttons.push({
      text: 'Cancelar',
      role: 'cancel'
    });

    return buttons;
  };

  return (
    <>
      <UserAvatar
        size={size}
        className={`${className} editable-avatar`}
        showEditButton={isLoggedIn}
        editable={isLoggedIn}
        onEditClick={handleEditClick}
        alt="Foto de perfil"
      />

      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        header="Foto de perfil"
        buttons={getActionSheetButtons()}
      />

      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Eliminar foto"
        message="¿Estás seguro de que quieres eliminar tu foto de perfil?"
        buttons={[
          {
            text: 'Cancelar',
            role: 'cancel'
          },
          {
            text: 'Eliminar',
            role: 'destructive',
            handler: removePhoto
          }
        ]}
      />

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        color={toastColor}
        position="bottom"
      />

      <ImageCropEditor
        isOpen={showCropEditor}
        onDidDismiss={handleCloseCropEditor}
        imageFile={imageToEdit || undefined}
        imageUrl={tempImageUrl || undefined}
        onImageCropped={handleImageCropped}
        targetSize={{ width: 500, height: 500 }}
        title="Recortar foto de perfil"
      />
    </>
  );
};

export default connect<EditableUserAvatarProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    userCed: state.user.userced,
    eventCode: state.user.eventcode,
    isLoggedIn: state.user.isLoggedin
  }),
  mapDispatchToProps: {
    setProfilePhotoUrl
  },
  component: EditableUserAvatar
});
